import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalStorageKey } from '../../constants/localStorageKey';
import { useAsyncAction, useLocalStorage, useUser } from '../../hooks';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import {
  GetBlacklistPermissionsResponse,
  PermissionItemsMap,
  UserPermissionType,
  useUserPermissions,
} from '../../hooks/useUserPermissions';
import { Icon, Icons, Typography } from '../../stories/atoms';
import { ColumnFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, Banner, BannerItemsType, Button } from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { commonTranslations, homeTranslations, widgetAccessControlTranslations } from '../../translations';
import {
  UserPermissionsMap,
  compareObjectKeys,
  getPermissionsListFromMap,
  getPermissionsMapFromList,
  getUniqueId,
  getUserPermissions,
  isFailureResponse,
} from '../../utils';
import { AreaWidgetType, areaWidgets } from '../areaOverview/util';
import { AreaWidgetsCategory, WidgetType, WidgetsCategory } from '../home/types';
import { widgetsCategoryToWidgetTypesMap } from '../home/util';
import { ButtonsContainer, StyledNote } from './WidgetAccessControlPage.styles';
import { WidgetCategory } from './widgetCategory/WidgetCategory';

type WidgetSectionDetails = {
  category: WidgetsCategory;
  widgetTypes: WidgetType[];
};

type AreaWidgetSectionDetails = {
  category: AreaWidgetsCategory;
  widgetTypes: AreaWidgetType[];
};

const widgetSectionsDetails: WidgetSectionDetails[] = [
  {
    category: WidgetsCategory.SALESEFFICIENCY,
    widgetTypes: widgetsCategoryToWidgetTypesMap.salesEfficiency,
  },
  {
    category: WidgetsCategory.CUSTOMERBASEVALUE,
    widgetTypes: widgetsCategoryToWidgetTypesMap.customerBaseValue,
  },
  {
    category: WidgetsCategory.CUSTOMEREXPERIENCE,
    widgetTypes: widgetsCategoryToWidgetTypesMap.customerExperience,
  },
];

const areaWidgetSectionDetails: AreaWidgetSectionDetails[] = [
  {
    category: AreaWidgetsCategory.OVERVIEW,
    widgetTypes: areaWidgets,
  },
];

const getPermissionTypeFromWidgetType = (widgetType: WidgetType | AreaWidgetType): UserPermissionType | null => {
  switch (widgetType) {
    //  Sales and Efficiency
    case WidgetType.ConversionRate:
      return 'conversionRateWidget';
    case WidgetType.HourlyTurnover:
      return 'salesPerformanceWidget';
    case WidgetType.PiecesPerReceipt:
      return 'avgPiecesPerReceiptWidget';
    case WidgetType.AveragePurchaseWidget:
      return 'avgPurchasePerReceiptWidget';
    case WidgetType.EfficiencyWidget:
      return 'efficiencyWidget';
    case WidgetType.SalesInsightsWidget:
      return 'salesInsightWidget';

    //  Customer Base Value
    case WidgetType.NewMembers:
      return 'newMembersWidget';
    case WidgetType.TopSellingGarment:
      return 'topSellingGarmentsWidget';
    case WidgetType.ShareOfReceipts:
      return 'shareOfReceiptWidget';
    case WidgetType.SCOShareOfReceipts:
      return 'scoShareOfReceipt';
    case WidgetType.AppCustomersWidget:
      return 'newAppCustomersWidget';

    //  Customer Experience
    case WidgetType.QueueLengthWidget:
      return 'queueLengthWidget';
    case WidgetType.CustomerFeedBackWidget:
      return 'customerFeedBackWidget';
    case WidgetType.RfidApprovalsWidget:
      return 'rfidApprovalsWidget';
    case WidgetType.RfidSalesRetagging:
      return 'rfidSalesWidget';
    case WidgetType.RfidCountWidget:
      return 'stockCountWidget';
    case WidgetType.CustomerEngagmentWidget:
      return 'customerEngagementWidget';
    case WidgetType.OrisCustomersWidget:
      return 'orisCustomerWidget';
    case WidgetType.HelpASaleWidget:
      return 'helpASaleWidget';
    case WidgetType.StockAccuracyWidget:
      return 'stockAccuracyWidget';
    case WidgetType.BopisWidget:
      return 'bopisWidget';

    // Area Widgets
    case AreaWidgetType.SalesOverview:
      return 'salesOverview';
    case AreaWidgetType.MembersOverview:
      return 'membersOverview';
    case AreaWidgetType.ConversionRateOverview:
      return 'conversionRateOverview';
    case AreaWidgetType.QueueCustomerOverview:
      return 'queueAndCustomerOverview';
    case AreaWidgetType.EfficiencyOverview:
      return 'efficiencyOverview';
    case AreaWidgetType.RfidOverview:
      return 'rfidOverview';
    case AreaWidgetType.BudgetPlanOverview:
      return 'budgetAndPlanOverview';
    case AreaWidgetType.KPIForecastOverview:
      return 'kpiTargetsOverview';
    default:
      return null;
  }
};

export const WidgetAccessControlPage: FC<unknown> = observer(() => {
  usePageEventTracker('widget_access_control');

  const isMobileTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const { t } = useTranslation();
  const { getItem } = useLocalStorage();
  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);
  const { widgetCategory: widgetCategoryTranslations } = homeTranslations;
  const selectedView = getItem(LocalStorageKey.SelectedView);

  const {
    set,
    get: { currentStoreId, profile, userPermissions: mobxPermissions, roleMatrix },
  } = useUser();

  const getIsActiveFromRoleMatrix = (widgetType: WidgetType | AreaWidgetType) => {
    const permissionType = getPermissionTypeFromWidgetType(widgetType);
    const requiredMatrix = roleMatrix.box.featurePermissions;

    if (!permissionType) return true;

    const permission = requiredMatrix[permissionType];

    return permission.isActive;
  };

  const { getBlacklistPermissions, postBlacklistPermissions, fetchUserPermissions } = useUserPermissions();

  const [currentBlacklistPermissionsMap, setCurrentBlacklistPermissionsMap] = useState<UserPermissionsMap>({});
  const [formBlacklistPermissionsMap, setFormBlacklistPermissionsMap] = useState<UserPermissionsMap>({});

  const getIsPermissionBlacklisted = (
    permissionsMap: UserPermissionsMap,
    widgetType: WidgetType | AreaWidgetType,
  ) => {
    const permissionType = getPermissionTypeFromWidgetType(widgetType);

    if (!permissionType) return true;

    return !!permissionsMap[permissionType];
  };

  const onSwitchToggle = (widgetType: WidgetType | AreaWidgetType) => {
    const permissionType = getPermissionTypeFromWidgetType(widgetType);

    if (!permissionType) return true;

    if (formBlacklistPermissionsMap[permissionType]) {
      const updatedPermissionsMap = { ...formBlacklistPermissionsMap };
      delete updatedPermissionsMap[permissionType];

      setFormBlacklistPermissionsMap(updatedPermissionsMap);
      return;
    }
    setFormBlacklistPermissionsMap({
      ...formBlacklistPermissionsMap,
      [permissionType]: true,
    });
  };

  const {
    result: blacklistPermissions,
    isLoading: blacklistPermissionsLoading,
    isError: blacklistPermissionsError,
    triggerAction: triggerFetchBlacklistPermissions,
  } = useAsyncAction<GetBlacklistPermissionsResponse>();

  const {
    result: updatedBlacklistPermissions,
    isLoading: updateBlacklistPermissionsLoading,
    isError: updateBlacklistPermissionsError,
    triggerAction: triggerUpdateBlacklistPermissions,
  } = useAsyncAction<GetBlacklistPermissionsResponse>();

  const {
    result: userPermissions,
    isLoading: userPermissionsLoading,
    isError: userPermissionsError,
    triggerAction: triggerLoadUserPermissions,
  } = useAsyncAction<PermissionItemsMap>(true);

  const fetchBlacklistPermissions = async () => {
    const response = await getBlacklistPermissions();

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const updateBlacklistPermissions = async () => {
    const permissions = getPermissionsListFromMap(formBlacklistPermissionsMap);

    const response = await postBlacklistPermissions({
      permissions,
    });

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const loadUserPermissions = async () => {
    const response = await fetchUserPermissions(profile.upn, currentStoreId);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const restoreButtonHandler = () => {
    setFormBlacklistPermissionsMap(currentBlacklistPermissionsMap);
  };

  useEffect(() => {
    triggerFetchBlacklistPermissions(fetchBlacklistPermissions);
  }, []);

  useEffect(() => {
    if (!blacklistPermissions) return;

    setCurrentBlacklistPermissionsMap(getPermissionsMapFromList(blacklistPermissions.permissions));
    setFormBlacklistPermissionsMap(getPermissionsMapFromList(blacklistPermissions.permissions));
  }, [blacklistPermissions]);

  useEffect(() => {
    if (!updatedBlacklistPermissions) return;

    setBannerItems([
      {
        id: getUniqueId(),
        label: t(
          widgetAccessControlTranslations.updateSuccessMessage.key,
          widgetAccessControlTranslations.updateSuccessMessage.defaultValue,
        ),
        type: 'success',
      },
    ]);

    setCurrentBlacklistPermissionsMap(getPermissionsMapFromList(updatedBlacklistPermissions.permissions));
    setFormBlacklistPermissionsMap(getPermissionsMapFromList(updatedBlacklistPermissions.permissions));

    triggerLoadUserPermissions(loadUserPermissions);
  }, [updatedBlacklistPermissions]);

  useEffect(() => {
    if (!updateBlacklistPermissionsLoading) return;

    setBannerItems([]);
  }, [updateBlacklistPermissionsLoading]);

  useEffect(() => {
    if (!updateBlacklistPermissionsError) return;

    setBannerItems([
      {
        id: getUniqueId(),
        label: t(
          widgetAccessControlTranslations.updateErrorMessage.key,
          widgetAccessControlTranslations.updateErrorMessage.defaultValue,
        ),
        type: 'error',
      },
    ]);
  }, [updateBlacklistPermissionsError]);

  useEffect(() => {
    if (!userPermissions) return;

    const apiUserPermissions = getUserPermissions(userPermissions);

    set({ userPermissions: apiUserPermissions });
  }, [userPermissions]);

  const isFormValid = !compareObjectKeys(currentBlacklistPermissionsMap, formBlacklistPermissionsMap);

  const isSwitchDisabled = !mobxPermissions.blacklistPermissionsForArea.canUpdate;

  return (
    <Page
      body={
        <div>
          <ColumnFlexContainer padding={isMobileTablet ? [2, 4, 8, 4] : [4, 8, 8, 8]} position="relative" flex={1}>
            <Banner isSticky items={bannerItems} onChange={(items) => setBannerItems(items)} />

            {blacklistPermissionsLoading && (
              <Animation
                title={t(
                  widgetAccessControlTranslations.loading.title.key,
                  widgetAccessControlTranslations.loading.title.defaultValue,
                )}
                animation={Animations.LOADING}
              />
            )}

            {!blacklistPermissionsLoading && blacklistPermissionsError && (
              <Animation
                title={t(
                  widgetAccessControlTranslations.error.title.key,
                  widgetAccessControlTranslations.error.title.defaultValue,
                )}
                subtitle={t(
                  widgetAccessControlTranslations.error.subTitle.key,
                  widgetAccessControlTranslations.error.subTitle.defaultValue,
                )}
                animation={Animations.SAD}
              />
            )}

            {userPermissionsError && (
              <Animation
                title={t(
                  widgetAccessControlTranslations.permissionLoadError.title.key,
                  widgetAccessControlTranslations.permissionLoadError.title.defaultValue,
                )}
                subtitle={t(
                  widgetAccessControlTranslations.permissionLoadError.subTitle.key,
                  widgetAccessControlTranslations.permissionLoadError.subTitle.defaultValue,
                )}
                button={{
                  isLoading: userPermissionsLoading,
                  label: t(commonTranslations.tryAgain.key, commonTranslations.tryAgain.defaultValue),
                  onClick: () => triggerLoadUserPermissions(loadUserPermissions),
                  buttonContentProps: {
                    iconOptions: {
                      icon: Icons.REFRESH,
                      color: 'white',
                    },
                  },
                }}
                animation={Animations.SAD}
              />
            )}

            {!blacklistPermissionsLoading &&
              !blacklistPermissionsError &&
              blacklistPermissions &&
              !userPermissionsError && (
                <>
                  <Typography type="h3" overflow={'visible'}>
                    {t(
                      widgetAccessControlTranslations.title.key,
                      widgetAccessControlTranslations.title.defaultValue,
                    )}
                  </Typography>

                  <Typography overflow={'visible'} gutterBottom>
                    {t(
                      widgetAccessControlTranslations.subTitle.key,
                      widgetAccessControlTranslations.subTitle.defaultValue,
                    )}{' '}
                    {blacklistPermissions ? blacklistPermissions.areaCode : ''}
                  </Typography>

                  {isSwitchDisabled && (
                    <StyledNote
                      backgroundColor="backgroundWarning"
                      borderRadius={[1]}
                      padding={[1, 2]}
                      alignItems="center"
                      margin={[0, 0, 3]}
                      data-testid="permission-disabled-note"
                    >
                      <Icon icon={Icons.WARNING} margin={[0, 2, 0, 0]} size="small" />
                      <Typography>
                        {t(
                          widgetCategoryTranslations.noteText.key,
                          widgetCategoryTranslations.noteText.defaultValue,
                        )}
                      </Typography>
                    </StyledNote>
                  )}
                  <ColumnFlexContainer
                    data-testid="widget-access-control-container"
                    margin={[0, 0, 5]}
                    gap={[5]}
                    maxHeight="calc(-250px + 100vh)"
                    overflow="auto"
                  >
                    {selectedView === 'store' &&
                      widgetSectionsDetails.map((widgetSectionDetails) => {
                        const { category, widgetTypes } = widgetSectionDetails;

                        return (
                          <WidgetCategory
                            key={category}
                            widgetTypes={widgetTypes}
                            category={category}
                            isSwitchDisabled={isSwitchDisabled}
                            onSwitchToggle={onSwitchToggle}
                            getIsActiveFromRoleMatrix={getIsActiveFromRoleMatrix}
                            handleGetIsPermissionBlacklisted={(widgetType) =>
                              getIsPermissionBlacklisted(formBlacklistPermissionsMap, widgetType)
                            }
                          />
                        );
                      })}
                    {selectedView === 'area' &&
                      areaWidgetSectionDetails.map((widgetSectionDetails) => {
                        const { category, widgetTypes } = widgetSectionDetails;

                        return (
                          <WidgetCategory
                            key={category}
                            widgetTypes={widgetTypes}
                            category={category}
                            isSwitchDisabled={isSwitchDisabled}
                            onSwitchToggle={onSwitchToggle}
                            getIsActiveFromRoleMatrix={getIsActiveFromRoleMatrix}
                            handleGetIsPermissionBlacklisted={(widgetType) =>
                              getIsPermissionBlacklisted(formBlacklistPermissionsMap, widgetType)
                            }
                          />
                        );
                      })}
                  </ColumnFlexContainer>
                  <ButtonsContainer width={'100%'} maxWidth={[100]}>
                    <Button
                      label={t(
                        widgetAccessControlTranslations.restoreButtonText.key,
                        widgetAccessControlTranslations.restoreButtonText.defaultValue,
                      )}
                      fullWidth
                      disabled={!isFormValid}
                      variant="secondary"
                      onClick={restoreButtonHandler}
                    />
                    <Button
                      label={t(
                        widgetAccessControlTranslations.saveButtonText.key,
                        widgetAccessControlTranslations.saveButtonText.defaultValue,
                      )}
                      fullWidth
                      isLoading={updateBlacklistPermissionsLoading || userPermissionsLoading}
                      disabled={!isFormValid}
                      onClick={() => triggerUpdateBlacklistPermissions(updateBlacklistPermissions)}
                    />
                  </ButtonsContainer>
                </>
              )}
          </ColumnFlexContainer>
        </div>
      }
    />
  );
});
